import type { ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MemoryRouter } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { ChevronDown, Code, FileDown, FileJson, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WikiContent } from '@/components/WikiContent';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { detectContentFormat, CONTENT_FORMAT_LABELS, type ContentFormat } from '@/lib/contentFormat';
import {
  absolutizeInternalLinks,
  buildStandaloneHtml,
  CONTENT_FORMAT_EXTENSIONS,
  downloadTextFile,
  slugifyFilename,
} from '@/lib/export';
import type { ViewFormat } from '@/hooks/useContentFormat';
import { cn } from '@/lib/utils';

interface ExportDocumentShellProps {
  title: string;
  /** Display name of the author, when resolved. */
  authorName?: string;
  /** The event being exported — supplies the byline date and the provenance footer. */
  event: NostrEvent;
  children: ReactNode;
}

/**
 * The header (title + byline) and footer (Nostr provenance) wrapped around
 * exported content. Rendered via React — never string interpolation — so
 * untrusted titles and names stay auto-escaped.
 *
 * A note on the safety of `renderToStaticMarkup` for export: the content
 * renderers emit React elements with auto-escaped text, drop raw-HTML
 * passthrough blocks, and route every URL through `sanitizeUrl` — so the
 * resulting markup is safe to ship as a standalone file. The
 * `MemoryRouter` supplies the router context the renderers' wikilink and
 * `nostr:` `<Link>`s require; their root-relative hrefs are rewritten to
 * absolute ones afterwards (`absolutizeInternalLinks`), so links in the
 * downloaded file lead back to this app instead of `file:///`.
 */
function ExportDocumentShell({ title, authorName, event, children }: ExportDocumentShellProps) {
  return (
    <>
      <header className="export-header">
        <h1>{title}</h1>
        <p className="export-byline">
          {authorName ? `by ${authorName} · ` : ''}
          {new Date(event.created_at * 1000).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </header>
      <main>{children}</main>
      <footer className="export-footer">
        <p>
          Published as a signed Nostr event (kind {event.kind}) by {nip19.npubEncode(event.pubkey)} ·
          exported from {window.location.origin}
        </p>
      </footer>
    </>
  );
}

interface ExportMenuProps {
  /** The wiki article or publication section to export. */
  event: NostrEvent;
  /** Display title — used for the filename and the exported document's heading. */
  title: string;
  /**
   * The format currently selected for viewing (`useContentFormat`'s
   * `selected`, which may be the reader's override). `'raw'` falls back to
   * detection — the export should be a readable document, not a hex dump.
   */
  format: ViewFormat;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * An "Export" dropdown for a single article or publication section:
 *
 *  - **Source** — the raw markup exactly as published (`.djot` / `.adoc` /
 *    `.md` / `.txt` matching the selected format).
 *  - **Web page** — a self-contained, styled HTML document rendered with
 *    the app's own renderers (same look as the live page, readable in any
 *    browser or e-reader, dark-mode aware).
 *  - **Signed event** — the full Nostr event as JSON (kind, tags,
 *    signature and all — the verifiable original, matching the "Raw" view).
 */
export function ExportMenu({ event, title, format, size = 'sm', className }: ExportMenuProps) {
  const { toast } = useToast();
  const author = useAuthor(event.pubkey);
  const authorName = author.data?.metadata?.name;

  const contentFormat: ContentFormat = format === 'raw' ? detectContentFormat(event.content) : format;
  const stem = slugifyFilename(title);

  const exportSource = () => {
    downloadTextFile(
      `${stem}.${CONTENT_FORMAT_EXTENSIONS[contentFormat]}`,
      event.content,
      'text/plain',
    );
  };

  const exportHtml = () => {
    try {
      const bodyHtml = renderToStaticMarkup(
        <MemoryRouter>
          <ExportDocumentShell title={title} authorName={authorName} event={event}>
            <WikiContent content={event.content} format={contentFormat} />
          </ExportDocumentShell>
        </MemoryRouter>,
      );
      const html = buildStandaloneHtml({
        title,
        description: event.tags.find(([n]) => n === 'summary')?.[1],
        bodyHtml: absolutizeInternalLinks(bodyHtml, window.location.origin),
      });
      downloadTextFile(`${stem}.html`, html, 'text/html');
    } catch {
      toast({ title: 'Export failed', description: 'The HTML document could not be generated.', variant: 'destructive' });
    }
  };

  const exportJson = () => {
    downloadTextFile(`${stem}.json`, JSON.stringify(event, null, 2), 'application/json');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size={size} className={cn('gap-1.5', className)} title="Export to file">
          <FileDown className="size-4" />
          Export
          <ChevronDown className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={exportSource}>
          <FileText className="size-4" />
          {CONTENT_FORMAT_LABELS[contentFormat]} source (.{CONTENT_FORMAT_EXTENSIONS[contentFormat]})
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={exportHtml}>
          <Code className="size-4" />
          Web page (.html)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={exportJson}>
          <FileJson className="size-4" />
          Signed event (.json)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
